if (!customElements.get('personalization-addons')) {
  customElements.define(
    'personalization-addons',
    class PersonalizationAddons extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.productInfo = this.closest('product-info');
        this.form =
          this.closest('form') ||
          (this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null) ||
          this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
        this.portalledModals = [];
        this.onModalChange = this.onModalChange.bind(this);
        this.onModalClick = this.onModalClick.bind(this);
        this.onModalInput = this.onModalInput.bind(this);
        this.onModalKeydown = this.onModalKeydown.bind(this);

        this.initModals();
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
        this.updateGroup();
        this.querySelectorAll('[data-personalization-item]').forEach((item) => {
          this.updateItem(item);
          this.updateSummary(item);
        });
        this.updateDynamicCheckout();
      }

      disconnectedCallback() {
        this.portalledModals?.forEach((modal) => {
          modal.removeEventListener('change', this.onModalChange);
          modal.removeEventListener('click', this.onModalClick);
          modal.removeEventListener('input', this.onModalInput);
          modal.remove();
        });
        document.removeEventListener('keydown', this.onModalKeydown);
        document.body.classList.remove('overflow-hidden');

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }
      }

      initModals() {
        this.querySelectorAll('[data-personalization-item]').forEach((item) => {
          const modal = item.querySelector('[data-personalization-modal]');
          if (!modal) return;

          const modalId =
            modal.id ||
            `PersonalizationModalPortal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
          modal.id = modalId;
          item.dataset.personalizationModalId = modalId;
          modal.personalizationOwner = item;
          modal.addEventListener('change', this.onModalChange);
          modal.addEventListener('click', this.onModalClick);
          modal.addEventListener('input', this.onModalInput);
          document.body.appendChild(modal);
          this.portalledModals.push(modal);
        });
      }

      onChange(event) {
        if (event.target.matches('[data-personalization-toggle]')) {
          this.updateGroup({ reset: !event.target.checked });
          this.updateDynamicCheckout();
          return;
        }

        const item = event.target.closest('[data-personalization-item]');
        if (!item) return;

        if (event.target.matches('[data-personalization-checkbox]')) {
          this.updateItem(item);
          this.updateDynamicCheckout();

          if (event.target.checked) {
            this.openModal(item);
          } else {
            this.closeModal(item, { returnFocus: false });
          }
        }
      }

      onModalChange(event) {
        const modal = event.target.closest('[data-personalization-modal]');
        const item = this.getModalOwner(modal);
        if (!item) return;

        if (event.target.matches('[data-personalization-font]')) {
          this.updateSummary(item);
        }
      }

      onClick(event) {
        const openButton = event.target.closest('[data-personalization-modal-open]');
        if (!openButton) return;

        this.openModal(openButton.closest('[data-personalization-item]'));
      }

      onModalClick(event) {
        const modal = event.target.closest('[data-personalization-modal]');
        const item = this.getModalOwner(modal);
        if (!item) return;

        const symbolButton = event.target.closest('[data-personalization-symbol]');
        if (symbolButton) {
          const textInput = this.getElement(item, '[data-personalization-text]');
          this.insertSymbol(textInput, symbolButton.dataset.personalizationSymbol || symbolButton.textContent || '');
          return;
        }

        const closeButton = event.target.closest('[data-personalization-modal-close], [data-personalization-modal-apply]');
        if (closeButton) {
          this.closeModal(item);
        }
      }

      onModalInput(event) {
        if (!event.target.matches('[data-personalization-text]')) return;

        const modal = event.target.closest('[data-personalization-modal]');
        const item = this.getModalOwner(modal);
        this.updateCount(event.target);
        this.updateSummary(item);

        if (event.target.value.trim()) {
          this.setError(item, '');
        }
      }

      updateItem(item) {
        const checkbox = item.querySelector('[data-personalization-checkbox]');
        const checked = this.isGroupEnabled() && Boolean(checkbox?.checked);
        const details = item.querySelector('[data-personalization-details]');
        const variant = item.querySelector('[data-personalization-variant]');
        const textInput = this.getElement(item, '[data-personalization-text]');
        const modalControls = this.getModal(item)?.querySelectorAll('input, textarea, button') || [];

        if (details) details.hidden = !checked;
        if (checkbox) checkbox.setAttribute('aria-expanded', checked ? 'true' : 'false');
        if (variant) variant.disabled = !checked;
        if (textInput) {
          textInput.disabled = !checked;
          textInput.required = checked;
          this.updateCount(textInput);
        }

        modalControls.forEach((control) => {
          if (control.matches('[data-personalization-modal-close], [data-personalization-modal-apply]')) return;
          control.disabled = !checked;
        });

        if (!checked) {
          this.setError(item, '');
        }

        item.classList.toggle('is-selected', checked);
      }

      updateGroup(options = { reset: false }) {
        const toggle = this.querySelector('[data-personalization-toggle]');
        const optionsPanel = this.querySelector('[data-personalization-options]');
        const enabled = this.isGroupEnabled();

        if (optionsPanel) optionsPanel.hidden = !enabled;
        if (toggle) toggle.setAttribute('aria-expanded', enabled ? 'true' : 'false');

        if (!enabled && options.reset) {
          this.querySelectorAll('[data-personalization-checkbox]').forEach((checkbox) => {
            checkbox.checked = false;
          });
          this.querySelectorAll('[data-personalization-item]').forEach((item) => {
            this.closeModal(item, { returnFocus: false });
          });
        }

        this.querySelectorAll('[data-personalization-item]').forEach((item) => this.updateItem(item));
      }

      isGroupEnabled() {
        const toggle = this.querySelector('[data-personalization-toggle]');
        return !toggle || toggle.checked;
      }

      updateSummary(item) {
        if (!item) return;

        const textInput = this.getElement(item, '[data-personalization-text]');
        const fontInput = this.getElement(item, '[data-personalization-font]:checked');
        const textSummary = item.querySelector('[data-personalization-summary]');
        const textSummaryValue = item.querySelector('[data-personalization-summary-text]');
        const fontSummary = item.querySelector('[data-personalization-font-summary]');
        const fontSummaryValue = item.querySelector('[data-personalization-summary-font]');
        const text = textInput?.value.trim() || '';
        const fontLabel = fontInput?.dataset.label || fontInput?.value || '';

        this.applyTextFont(item, fontInput);

        if (textSummary && textSummaryValue) {
          textSummary.hidden = !text;
          textSummaryValue.textContent = text;
        }

        if (fontSummary && fontSummaryValue) {
          fontSummary.hidden = !fontLabel;
          fontSummaryValue.textContent = fontLabel;
        }

        const description = item.querySelector('.personalization-addons__description');
        if (description) {
          description.hidden = Boolean(text);
        }
      }

      applyTextFont(item, fontInput = null) {
        const textInput = this.getElement(item, '[data-personalization-text]');
        if (!textInput) return;

        const fontKey = this.getFontKey(fontInput || this.getElement(item, '[data-personalization-font]:checked'));
        textInput.classList.remove(
          'personalization-addons__text--font-elegant',
          'personalization-addons__text--font-simple',
          'personalization-addons__text--font-script',
          'personalization-addons__text--font-bold'
        );

        if (fontKey) {
          textInput.classList.add(`personalization-addons__text--font-${fontKey}`);
        }
      }

      getFontKey(fontInput) {
        if (!fontInput) return '';

        const value = (fontInput.value || '').toLowerCase();
        const label = (fontInput.dataset.label || '').toLowerCase();
        const fontName = `${value} ${label}`
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

        if (fontName.includes('elegant')) return 'elegant';
        if (fontName.includes('simple') || fontName.includes('jednoduch')) return 'simple';
        if (fontName.includes('script') || fontName.includes('pisan')) return 'script';
        if (fontName.includes('bold') || fontName.includes('vyraz')) return 'bold';

        return '';
      }

      getSelections() {
        if (!this.isGroupEnabled()) return [];

        return Array.from(this.querySelectorAll('[data-personalization-item]'))
          .filter((item) => item.querySelector('[data-personalization-checkbox]')?.checked)
          .map((item) => {
            const variant = item.querySelector('[data-personalization-variant]');
            const textInput = this.getElement(item, '[data-personalization-text]');
            const fontInput = this.getElement(item, '[data-personalization-font]:checked');

            return {
              addonType: item.dataset.addonType || `personalization_${item.dataset.personalizationSide || 'text'}`,
              side: item.dataset.personalizationSide || '',
              sideLabel: item.dataset.sideLabel || '',
              variantId: Number(variant?.value),
              text: textInput?.value.trim() || '',
              textInput,
              font: fontInput?.value || '',
              fontLabel: fontInput?.dataset.label || fontInput?.value || '',
            };
          })
          .filter((selection) => selection.variantId);
      }

      validate() {
        if (!this.isGroupEnabled()) return null;

        const selectedItems = Array.from(this.querySelectorAll('[data-personalization-item]')).filter((item) => {
          return item.querySelector('[data-personalization-checkbox]')?.checked;
        });

        for (const item of selectedItems) {
          const variant = item.querySelector('[data-personalization-variant]');
          const textInput = this.getElement(item, '[data-personalization-text]');

          if (!Number(variant?.value)) {
            return { error: this.dataset.addError || window.cartStrings?.error || '', element: item };
          }

          if (!textInput?.value.trim()) {
            const error = this.dataset.textError || '';
            this.setError(item, error);
            return { error, element: textInput };
          }

          this.setError(item, '');
        }

        return null;
      }

      clearErrors() {
        this.querySelectorAll('[data-personalization-item]').forEach((item) => this.setError(item, ''));
      }

      setError(item, message) {
        if (typeof item === 'string') {
          item = this.querySelector(`[data-personalization-side="${item}"]`);
        }
        if (!item) return;

        const itemError = item.querySelector('[data-personalization-item-error]');
        const textError = this.getElement(item, '[data-personalization-text-error]');
        const textInput = this.getElement(item, '[data-personalization-text]');

        [itemError, textError].forEach((errorElement) => {
          if (!errorElement) return;
          errorElement.textContent = message;
          errorElement.hidden = !message;
        });

        if (textInput) {
          textInput.toggleAttribute('aria-invalid', Boolean(message));
        }

        if (message && item.querySelector('[data-personalization-checkbox]')?.checked) {
          this.openModal(item);
        }
      }

      insertSymbol(textInput, symbol) {
        if (!textInput || textInput.disabled || !symbol) return;

        const start = textInput.selectionStart ?? textInput.value.length;
        const end = textInput.selectionEnd ?? textInput.value.length;
        const maxLength = textInput.maxLength > -1 ? textInput.maxLength : Infinity;
        const available = maxLength - (textInput.value.length - (end - start));
        if (available <= 0) return;

        const insertion = symbol.slice(0, available);
        textInput.value = `${textInput.value.slice(0, start)}${insertion}${textInput.value.slice(end)}`;
        const cursorPosition = start + insertion.length;
        textInput.setSelectionRange(cursorPosition, cursorPosition);
        textInput.focus();
        textInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      getModal(item) {
        if (!item) return null;

        return item.dataset.personalizationModalId
          ? document.getElementById(item.dataset.personalizationModalId)
          : item.querySelector('[data-personalization-modal]');
      }

      getModalOwner(modal) {
        return modal?.personalizationOwner || null;
      }

      getElement(item, selector) {
        return this.getModal(item)?.querySelector(selector) || item?.querySelector(selector);
      }

      openModal(item) {
        const modal = this.getModal(item);
        if (!modal) return;

        this.activeItem = item;
        this.lastFocusedElement = document.activeElement;
        modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onModalKeydown);
        this.updateItem(item);
        this.updateSummary(item);

        const dialog = modal.querySelector('[data-personalization-modal-dialog]');
        const focusTarget = modal.querySelector('[data-personalization-text]') || modal.querySelector('[data-personalization-font]:checked') || dialog;

        requestAnimationFrame(() => {
          if (typeof trapFocus === 'function' && dialog) {
            trapFocus(dialog, focusTarget);
          } else {
            focusTarget?.focus();
          }
        });
      }

      closeModal(item = this.activeItem, options = { returnFocus: true }) {
        const modal = this.getModal(item);
        if (!modal || modal.hidden) return;

        modal.hidden = true;
        document.removeEventListener('keydown', this.onModalKeydown);
        if (!this.portalledModals.some((portalledModal) => !portalledModal.hidden)) {
          document.body.classList.remove('overflow-hidden');
        }

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }

        this.updateSummary(item);

        if (options.returnFocus !== false) {
          const returnTarget = item?.querySelector('[data-personalization-modal-open]') || this.lastFocusedElement;
          if (returnTarget && typeof returnTarget.focus === 'function') returnTarget.focus();
        }
      }

      onModalKeydown(event) {
        if (event.key !== 'Escape') return;

        event.preventDefault();
        this.closeModal();
      }

      updateCount(textInput) {
        if (!textInput) return;

        const counter =
          textInput.closest('[data-personalization-modal]')?.querySelector('[data-personalization-count]') ||
          this.querySelector('[data-personalization-count]');
        if (!counter) return;

        const template = this.dataset.countTemplate || '__count__/__max__';
        counter.textContent = template
          .replace('__count__', textInput.value.length)
          .replace('__max__', textInput.maxLength || '');
      }

      updateDynamicCheckout() {
        if (this.dataset.hideDynamicCheckout !== 'true' || !this.form) return;

        const scope = this.productInfo || this.form;
        const shouldHide = Boolean(
          scope.querySelector(
            '[data-addon-checkbox]:checked, [data-personalization-toggle]:checked, [data-personalization-checkbox]:checked'
          )
        );
        this.form.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]').forEach((button) => {
          button.hidden = shouldHide;
          button.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        });
      }
    }
  );
}
